import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = { class: "ml-auto" }
const _hoisted_4 = { class: "text-center" }

import {onBeforeUnmount, onMounted, reactive, ref, markRaw} from "vue";
import { BryntumGrid } from "@bryntum/grid-vue-3";
import {Menu, Store} from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter.vue";
import { useGlobalStore } from "@/stores/GlobalStore";
import {ErrorLocationType, PopupType, TableType} from '@/utilities/enums/Enums';
import { $t } from "@/utilities/i18n/config";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import { GeneralResponse } from "@/models/api/Responses";
import Error from "@/components/Error.vue";
import {useI18n} from "vue-i18n";
import {defaultSpaceAroundTableOnPage} from "@/AppConfig";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {PronunciationSetting} from "@/models/setting/PronunciationSetting";
import {SettingService} from "@/utilities/services/entities/SettingService";



export default /*#__PURE__*/_defineComponent({
  __name: 'SpokenTextTab',
  setup(__props) {

const gridStore = new Store();
const globalStore = useGlobalStore();

const grid = ref(null);
const { t } = useI18n();
const settingService = new SettingService();

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage-80}px`;
};

const useGridConfig = {
  headerMenuFeature: false,
  cellTooltipFeature: {
    hoverDelay: 300,
    textContent: true,
		hideOnDelegateChange: true,
  },
  relayStoreEvents: true,
  enableTextSelection: true,
  selectionMode: { row: false, cell: false },
  height: currentWindowHeight,
  columns : {
    data: [
      { id: 'internalIdentification', field: 'internalIdentification', text: t('settings.grid.intern'), flex: 1, },
			{
				id: 'helpText',
				field: '',
				text: "",
				width: 50,
				sortable: false,
				resizable: false,
				minWidth: 50,
				type: "template",
				tooltipRenderer : ({record}) => `${record.data.helpText}`,
				template: ({record}) => {
					if(record.data.helpText !== null || record.data.helpText !== "")
						return `<div class='gridTooltipButton'><span class='icon-info'></span></div>`;
					return
				},
			},
			{
        id: 'name',
        field: 'name',
        text: t('settings.grid.description'),
        flex: 1,
      },
      {
        id: 'value',
        field: 'value',
        text: t('settings.grid.speachText'),
        flex: 2.5,
				cellCls: "multiline",
				autoHeight: true,
			},
      {
        id: 'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.spokenText], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    datachange(){
      globalStore.currentItems = gridStore.count;
      globalStore.allItems = gridStore.originalCount;
    }
  },
}
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: t("global.edit"),
        cls: 'color',
        icon: 'icon-editieren',
        weight: 100,
        onItem : ({ menu }) => {
          globalStore.openPopup({
            data: JSON.parse(JSON.stringify(menu.record)),
            action:updatePronunciationSetting,
            resetButton:false,
            title:$t("popups.settings.spokenText.edit"),
            type:PopupType.settingsEditSpokenText
          });
        }
      },
    ],
  });

  menu.setRecord(record);

  return menu;
};

const updatePronunciationSetting = async () => {
  const pronunciationSetting = globalStore.modal.contentObject as PronunciationSetting;

  const pronunciationUpdateResp = await settingService.putUpdatePronunciationSetting(pronunciationSetting);
  if (pronunciationUpdateResp === null || pronunciationUpdateResp === undefined || pronunciationUpdateResp.statusCode !== 0){
    ErrorHandler.handleError(pronunciationUpdateResp as GeneralResponse, ErrorLocationType.popup);
    return;
  }

  // Gleicher Fix wie bei SystemSettingsTab.vue: Update statt Add!
  const cleanSetting = JSON.parse(JSON.stringify(pronunciationSetting));
  const record = gridStore.getById(cleanSetting.id);
  if (record) {
    record.set(cleanSetting);
  } else {
    gridStore.add(cleanSetting);
  }

  globalStore.closeModal();
  globalStore.showSuccess(t('global.saved'));
}

const setGridData = async ()=>{
  // start spinner
  globalStore.gridDataIsLoading = true;

  const spokenTextResp = await settingService.getPronunciationSettings();

  if (spokenTextResp === null || spokenTextResp === undefined || spokenTextResp.statusCode !== 0) {
    ErrorHandler.handleError(spokenTextResp, ErrorLocationType.table);
    return;
  }

  gridStore.setStoreData(spokenTextResp.settingInfo);

  globalStore.reorderColumns(TableType[TableType.spokenText]);

  globalStore.closeModal();
  // stop spinner
  globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(ref(markRaw(gridStore)));
  
  if (grid.value?.instance?.columns) {
    globalStore.setGridColumnStore(ref(markRaw(grid.value.instance.columns)));
  }

  await setGridData();
});

onBeforeUnmount(()=>{
  window.removeEventListener('resize', recalcGridHeight);

  globalStore.setGridStore(null);
  globalStore.setGridColumnStore(null);

  globalStore.currentItems = 0;
  globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, { readOnly: true }), null, 16)
      ])
    ], 512), [
      [_vShow, !_unref(globalStore).gridDataIsLoading]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_4, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, _unref(globalStore).gridDataIsLoading]
    ])
  ], 64))
}
}

})